import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextArea } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface Outcome {
  id: string;
  outcome: string;
  whoBenefits: string;
  howWeKnow: string;
}

interface SuccessSignal {
  id: string;
  type: 'behavior' | 'quality' | 'reach';
  baseline: string;
  target: string;
}

export default function PurposeOutcomesPage() {
  const [outcomes, setOutcomes] = useState<Outcome[]>([
    { id: '1', outcome: '', whoBenefits: '', howWeKnow: '' },
  ]);
  const [successSignals, setSuccessSignals] = useState<SuccessSignal[]>([
    { id: '1', type: 'behavior', baseline: '', target: '' },
    { id: '2', type: 'quality', baseline: '', target: '' },
    { id: '3', type: 'reach', baseline: '', target: '' },
  ]);
  const [antiGoals, setAntiGoals] = useState<string[]>(['']);

  const addOutcome = () => {
    setOutcomes([...outcomes, { id: Date.now().toString(), outcome: '', whoBenefits: '', howWeKnow: '' }]);
  };

  const updateOutcome = (id: string, field: keyof Outcome, value: string) => {
    setOutcomes(outcomes.map(o => o.id === id ? { ...o, [field]: value } : o));
  };

  const updateSignal = (id: string, field: keyof SuccessSignal, value: string | 'behavior' | 'quality' | 'reach') => {
    setSuccessSignals(signals => signals.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const addAntiGoal = () => {
    setAntiGoals([...antiGoals, '']);
  };

  const updateAntiGoal = (index: number, value: string) => {
    const updated = [...antiGoals];
    updated[index] = value;
    setAntiGoals(updated);
  };

  return (
    <TaskPage
      phase="engage"
      title="Purpose & Desired Outcomes"
      pageNumber={1}
      instruction="Start with why before what. If this is wildly successful in 90 days, what is true that isn't true today?"
      whyItMatters="Focusing on concrete signals and anti-goals provides a worked example of 'what good looks like' and light scaffolding for novices."
      coachRail={{
        timing: "10 minutes",
        tips: "Choose 1-3 desired outcomes. Define success signals with baseline and target.",
        debrief: "What would make this wildly successful?"
      }}
    >
      <div className="space-y-6">
        {/* Desired Outcomes */}
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Desired Outcomes (Choose 1-3)
          </h3>
          <p className="text-sm text-[var(--text-sec)] mb-4">For each outcome: Outcome + who benefits + how we'll know</p>
          <div className="space-y-4">
            {outcomes.map((outcome, index) => (
              <div key={outcome.id} className="border border-[var(--rule)] p-4 bg-[var(--bg-raised)]">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Outcome {index + 1}
                  </label>
                  <TextArea
                    value={outcome.outcome}
                    onChange={(e) => updateOutcome(outcome.id, 'outcome', e.target.value)}
                    placeholder="What outcome will we achieve?"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      Who benefits?
                    </label>
                    <TextArea
                      value={outcome.whoBenefits}
                      onChange={(e) => updateOutcome(outcome.id, 'whoBenefits', e.target.value)}
                      placeholder="Who will benefit from this outcome?"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      How we'll know
                    </label>
                    <TextArea
                      value={outcome.howWeKnow}
                      onChange={(e) => updateOutcome(outcome.id, 'howWeKnow', e.target.value)}
                      placeholder="How will we measure success?"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            ))}
            {outcomes.length < 3 && (
              <button
                onClick={addOutcome}
                className="w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
              >
                + Add another outcome
              </button>
            )}
          </div>
        </div>

        {/* Success Signals */}
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-sec)] mb-4">
            Success Signals (Pick three, write baseline → target)
          </h3>
          <div className="space-y-4">
            {successSignals.map((signal) => (
              <div key={signal.id} className="bg-[var(--bg-raised)] p-4 border border-[var(--slate)]">
                <label className="block text-sm font-medium text-[var(--text-ter)] mb-2">
                  {signal.type === 'behavior' && "Behavior we'll see (leading indicator)"}
                  {signal.type === 'quality' && "Quality we'll improve (e.g., cycle time/defect rate)"}
                  {signal.type === 'reach' && "Reach/adoption we'll grow"}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={signal.baseline}
                    onChange={(e) => updateSignal(signal.id, 'baseline', e.target.value)}
                    placeholder="Baseline"
                    className="flex-1 px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-[var(--text-ter)] font-semibold">→</span>
                  <input
                    type="text"
                    value={signal.target}
                    onChange={(e) => updateSignal(signal.id, 'target', e.target.value)}
                    placeholder="Target"
                    className="flex-1 px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Anti-goals */}
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Anti-goals (To prevent scope creep)
          </h3>
          <p className="text-sm text-[var(--text-sec)] mb-4">We are not trying to…</p>
          <div className="space-y-3">
            {antiGoals.map((goal, index) => (
              <TextArea
                key={index}
                value={goal}
                onChange={(e) => updateAntiGoal(index, e.target.value)}
                placeholder={`Anti-goal ${index + 1}`}
                rows={2}
              />
            ))}
            {antiGoals.length < 3 && (
              <button
                onClick={addAntiGoal}
                className="w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
              >
                + Add another anti-goal
              </button>
            )}
          </div>
        </div>

        <Callout type="tip" phase="engage">
          <p className="text-sm">Focus on concrete, observable signals rather than vague aspirations. This helps everyone understand what success looks like.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}
